import type { MetaFunction } from '@remix-run/node';
import { formatDate } from '~/data/dates';
import { ATTACHED_QUOTE } from '~/data/way-in-seed';

export const meta: MetaFunction = () => [
  { title: `Quote ${ATTACHED_QUOTE.number} | Science Exchange` },
];

// The file the requester attached to the invite, opened from the chip on the first
// way-in screen. It stands in for the uploaded document until the case lookup exists.
export default function AttachedQuote() {
  const quote = ATTACHED_QUOTE;
  const total = quote.lines.reduce(
    (sum, currentLine) => sum + currentLine.quantity * currentLine.unitPrice,
    0,
  );
  const formatMoney = (amount: number) =>
    amount.toLocaleString('en-US', { style: 'currency', currency: quote.currency });

  return (
    <main className="min-h-screen bg-brand-ground px-4 py-8 font-body text-brand-ink md:py-14">
      <article className="mx-auto max-w-column rounded-card border border-brand-line bg-white px-5 py-7 md:px-10 md:py-10">
        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-brand-line pb-6">
          <div>
            <p className="font-display text-brand-eyebrow uppercase text-brand-muted">Quote</p>
            <h1 className="m-0 mt-1 font-display text-brand-option">{quote.from}</h1>
            <p className="m-0 mt-1 text-brand-meta text-brand-muted">{quote.study}</p>
          </div>
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-brand-meta">
            <dt className="text-brand-muted">Number</dt>
            <dd className="m-0">{quote.number}</dd>
            <dt className="text-brand-muted">Issued</dt>
            <dd className="m-0">{formatDate(quote.issuedOn)}</dd>
            <dt className="text-brand-muted">Valid until</dt>
            <dd className="m-0">{formatDate(quote.validUntil)}</dd>
          </dl>
        </header>

        <p className="mt-6 text-brand-meta">
          Prepared for <strong>{quote.to}</strong>, attention {quote.attention}
        </p>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full min-w-[480px] border-collapse text-brand-meta">
            <thead>
              <tr className="border-b border-brand-line-strong text-left text-brand-muted">
                <th className="py-2 pr-4 font-semibold">Description</th>
                <th className="py-2 pr-4 text-right font-semibold">Qty</th>
                <th className="py-2 pr-4 text-right font-semibold">Unit price</th>
                <th className="py-2 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {quote.lines.map((currentLine) => (
                <tr key={currentLine.description} className="border-b border-brand-line">
                  <td className="py-2.5 pr-4">{currentLine.description}</td>
                  <td className="py-2.5 pr-4 text-right tabular-nums">{currentLine.quantity}</td>
                  <td className="py-2.5 pr-4 text-right tabular-nums">
                    {formatMoney(currentLine.unitPrice)}
                  </td>
                  <td className="py-2.5 text-right tabular-nums">
                    {formatMoney(currentLine.quantity * currentLine.unitPrice)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="pt-4 text-right font-semibold">
                  Total ({quote.currency})
                </td>
                <td className="pt-4 text-right font-semibold tabular-nums">{formatMoney(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="mt-8 text-brand-meta text-brand-muted">
          Pricing excludes shipping of samples to the testing facility. Work begins on receipt of a
          purchase order.
        </p>
      </article>
    </main>
  );
}
